/**
 * Recents Redux Slice
 * Manages recent items state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  RecentItem,
  TrackAccessRequest,
  QueryRecentParams,
} from '../../api/types/RecentTypes';
import { recentsApi } from '../../api/endpoints/recentsApi';

// ==================== State Interface ====================

interface RecentsState {
  // All recent items
  recentItems: RecentItem[];

  // Workspace-specific recent items
  workspaceRecentItems: { [workspaceId: string]: RecentItem[] };

  // Popular items (by access count)
  popularItems: RecentItem[];

  // UI state
  loading: boolean;
  error: string | null;
  trackingAccess: boolean;
}

const initialState: RecentsState = {
  recentItems: [],
  workspaceRecentItems: {},
  popularItems: [],
  loading: false,
  error: null,
  trackingAccess: false,
};

// ==================== Async Thunks ====================

/**
 * Track item access
 */
export const trackAccess = createAsyncThunk(
  'recents/trackAccess',
  async (data: TrackAccessRequest) => {
    const response = await recentsApi.trackAccess(data);
    return response;
  },
);

/**
 * Fetch all recent items
 */
export const fetchRecentItems = createAsyncThunk(
  'recents/fetchRecentItems',
  async (params?: QueryRecentParams) => {
    const items = await recentsApi.getRecentItems(params);
    return items;
  },
);

/**
 * Fetch recent items for a workspace
 */
export const fetchWorkspaceRecentItems = createAsyncThunk(
  'recents/fetchWorkspaceRecentItems',
  async ({ workspaceId, params }: { workspaceId: string; params?: QueryRecentParams }) => {
    const items = await recentsApi.getWorkspaceRecentItems(workspaceId, params);
    return { workspaceId, items };
  },
);

/**
 * Fetch most popular items
 */
export const fetchPopularItems = createAsyncThunk(
  'recents/fetchPopularItems',
  async (params?: QueryRecentParams) => {
    const items = await recentsApi.getMostPopularItems(params);
    return items;
  },
);

/**
 * Remove a recent item
 */
export const removeRecentItem = createAsyncThunk(
  'recents/removeRecentItem',
  async (recentItemId: string) => {
    await recentsApi.removeRecentItem(recentItemId);
    return recentItemId;
  },
);

/**
 * Clear all recent items
 */
export const clearRecentItems = createAsyncThunk(
  'recents/clearRecentItems',
  async (params?: QueryRecentParams) => {
    await recentsApi.clearRecentItems(params);
    return params?.type;
  },
);

// ==================== Slice ====================

const recentsSlice = createSlice({
  name: 'recents',
  initialState,
  reducers: {
    // Clear error
    clearError(state) {
      state.error = null;
    },

    // Clear workspace recent items from cache
    clearWorkspaceRecents(state, action: PayloadAction<string>) {
      delete state.workspaceRecentItems[action.payload];
    },
  },
  extraReducers: (builder) => {
    // ==================== Track Access ====================
    builder
      .addCase(trackAccess.pending, (state) => {
        state.trackingAccess = true;
      })
      .addCase(trackAccess.fulfilled, (state) => {
        state.trackingAccess = false;
        // Note: We don't update the list here, it will be refreshed on next fetch
      })
      .addCase(trackAccess.rejected, (state, action) => {
        state.trackingAccess = false;
        // Silently fail - tracking is a background operation
        console.warn('Failed to track access:', action.error.message);
      });

    // ==================== Fetch Recent Items ====================
    builder
      .addCase(fetchRecentItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentItems.fulfilled, (state, action) => {
        state.loading = false;
        state.recentItems = action.payload;
      })
      .addCase(fetchRecentItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch recent items';
      });

    // ==================== Fetch Workspace Recent Items ====================
    builder
      .addCase(fetchWorkspaceRecentItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaceRecentItems.fulfilled, (state, action) => {
        state.loading = false;
        state.workspaceRecentItems[action.payload.workspaceId] = action.payload.items;
      })
      .addCase(fetchWorkspaceRecentItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch workspace recent items';
      });

    // ==================== Fetch Popular Items ====================
    builder
      .addCase(fetchPopularItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPopularItems.fulfilled, (state, action) => {
        state.loading = false;
        state.popularItems = action.payload;
      })
      .addCase(fetchPopularItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch popular items';
      });

    // ==================== Remove Recent Item ====================
    builder
      .addCase(removeRecentItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeRecentItem.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from all lists
        state.recentItems = state.recentItems.filter((item) => item.id !== action.payload);
        state.popularItems = state.popularItems.filter((item) => item.id !== action.payload);
        // Remove from workspace lists
        Object.keys(state.workspaceRecentItems).forEach((workspaceId) => {
          state.workspaceRecentItems[workspaceId] = state.workspaceRecentItems[
            workspaceId
          ].filter((item) => item.id !== action.payload);
        });
      })
      .addCase(removeRecentItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to remove recent item';
      });

    // ==================== Clear Recent Items ====================
    builder
      .addCase(clearRecentItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearRecentItems.fulfilled, (state, action) => {
        state.loading = false;
        const itemType = action.payload;

        if (itemType) {
          // Clear specific type
          state.recentItems = state.recentItems.filter((item) => item.itemType !== itemType);
          state.popularItems = state.popularItems.filter((item) => item.itemType !== itemType);
          // Clear from workspace lists
          Object.keys(state.workspaceRecentItems).forEach((workspaceId) => {
            state.workspaceRecentItems[workspaceId] = state.workspaceRecentItems[
              workspaceId
            ].filter((item) => item.itemType !== itemType);
          });
        } else {
          // Clear all
          state.recentItems = [];
          state.popularItems = [];
          state.workspaceRecentItems = {};
        }
      })
      .addCase(clearRecentItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to clear recent items';
      });
  },
});

// ==================== Exports ====================

export const { clearError, clearWorkspaceRecents } = recentsSlice.actions;

export default recentsSlice.reducer;
